import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { useKeycloak } from "@react-keycloak/web";
import Loader from '../../../../../../Atoms/Loader';
import Swal from 'sweetalert2';

import Tooltip from '../../../../../../Atoms/Tooltip';
import ButtonMain from '../../../../../../Atoms/ButtonMain';
import ButtonSecondary from '../../../../../../Atoms/ButtonSecondary';
import MemberAvatar from '../../../../../../Molecules/MemberAvatar';

import { getTimeAgo } from '../../../../../../../utils/sharedFunctions';
import DeliverableService from '../../../../../../../services/Deliverable/DeliverableService';

import './styles.css';

export default function PublishModal({
    show,
    onHide,
    file,
    projectName,
    companyName,
    projectId,
    onPublishSuccess,
}) {
    const { keycloak } = useKeycloak();
    const [deliverableName, setDeliverableName] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [loading, setLoading] = useState(false);

    const MAX_NAME_LENGTH = 70;
    const KEYCLOACK_TOKEN = keycloak.token;

    useEffect(() => {
        if (file) {
            setDeliverableName(file.name || '');
        }
    }, [file]);

    const handleClose = () => {
        setDeliverableName('');
        setKeywords([]);
        setKeywordInput('');
        onHide();
    };

    const handleNameChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_NAME_LENGTH) {
            setDeliverableName(value);
        }
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const newKeyword = keywordInput.trim();
            if (newKeyword && !keywords.includes(newKeyword)) {
                setKeywords([...keywords, newKeyword]);
            }
            setKeywordInput('');
        }
    };

    const removeKeyword = (keywordToRemove) => {
        setKeywords(keywords.filter((kw) => kw !== keywordToRemove));
    };

    const handlePublish = async () => {
        setLoading(true);
        if (!file?.id || !deliverableName || keywords.length === 0) {
            Swal.fire({
                title: 'Erro ao publicar',
                text: 'Por favor, preencha todos os campos obrigatórios.',
                background: '#27272A',
                color: '#fff',
                timer: 2500,
                position: 'top-end',
                showConfirmButton: false,
            });
            setLoading(false);
            return;
        }
            
        const payload = {
            name: deliverableName.replace(/\s+/g, ' '),
            assetId: file?.id,
            keywords: keywords
        }

        try {    
            const response = await DeliverableService.publishDeliverable(projectId, payload, KEYCLOACK_TOKEN);

            if (response) {
                if (onPublishSuccess) {
                    await onPublishSuccess();
                }
                Swal.fire({
                    icon: 'success',
                    title: 'Entregável publicado com sucesso',
                    text: 'O entregável foi publicado com sucesso.',
                    background: '#27272A',
                    color: '#fff',
                    timer: 1800,
                    showConfirmButton: false,
                });
            } 
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Erro ao publicar',
                text: 'Erro ao publicar o entregável. Tente novamente.',
                background: '#27272A',
                color: '#fff',
                timer: 1800,
                showConfirmButton: false,
            });
            console.error('Error publishing deliverable:', error);
        } finally {
            setLoading(false);
            handleClose();
        }
        
    };

    const formatOrigin = (origin) => {
        if (!origin) return '';

        switch (origin) {
            case 'BRAINSTORM':
                return 'Brainstorm';
            case 'ATTACHMENT':
                return 'Anexo';
            case 'WORKSPACE':
                return 'Workspace';
            default:
                return origin;
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="publish-modal"
            size="md"
        >
            <Modal.Header className="publish-modal-header">
                <Modal.Title className="publish-modal-title">Publicar entregável</Modal.Title>
                <button
                    type="button"
                    className="publish-modal-close"
                    onClick={handleClose}
                    aria-label="Close"
                >
                    ×
                </button>
            </Modal.Header>

            <Modal.Body className="publish-modal-body">
                {/* Project Info */}
                <div className="publish-modal-project-info">
                    <h2 className="publish-modal-project-name">{projectName}</h2>
                    {companyName && (
                        <div className="publish-modal-company-badge">
                            <span className="publish-modal-company-name">{companyName}</span>
                        </div>
                    )}
                </div>

                <p className="publish-modal-subtitle">
                    Configure antes de publicar para o Cliente
                </p>

                {/* File Card */}
                {file && (
                    <div className="publish-modal-file-card">
                        <div className="publish-modal-file-info">
                            <p className="publish-modal-file-name">{file.name}</p>
                            <span className="publish-modal-file-origin">
                                {formatOrigin(file.origin)}
                            </span>
                        </div>
                        <div className="publish-modal-file-user">
                            <MemberAvatar member={file.processProfile?.user} size="32px" />
                            <div className="publish-modal-file-user-details">
                                <span className="publish-modal-file-username">
                                    {file.processProfile?.user?.username || 'Usuário'}
                                </span>
                                <span className="publish-modal-file-time">
                                    {file.updatedAt ? getTimeAgo(file.updatedAt) : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Section */}
                <div className="publish-modal-form">
                    <h3 className="publish-modal-form-title">Dados da entrega</h3>

                    {/* Deliverable Name Input */}
                    <div className="publish-modal-field">
                        <label className="publish-modal-label">Nome do entregável*</label>
                        <div className="publish-modal-input-wrapper">
                            <input
                                type="text"
                                className="publish-modal-input"
                                value={deliverableName}
                                onChange={handleNameChange}
                                placeholder="Nome do entregável"
                            />
                            <button
                                type="button"
                                className="publish-modal-input-clear"
                                onClick={() => setDeliverableName('')}
                                aria-label="Clear"
                            >
                                ×
                            </button>
                        </div>
                        <span className="publish-modal-char-count">
                            {MAX_NAME_LENGTH - deliverableName.length} caracteres
                        </span>
                    </div>

                    {/* Keywords Input */}
                    <div className="publish-modal-field">
                        <div className="publish-modal-label-row">
                            <label className="publish-modal-label">Palavra-chave</label>
                            <Tooltip
                                text="Ajude a Aira a entender este documento. Adicione palavras-chave para que a Aira recomende este conteúdo nas conversas com o cliente."
                                tooltipStyle={{ minWidth: '200px', left: 'auto', right: '0' }}
                            >
                                <HiOutlineInformationCircle color="#C27AFF" size={16} />
                            </Tooltip>
                        </div>
                        <span className="publish-modal-field-subtitle">
                            O que a IA deve buscar no documento
                        </span>

                        <div className="publish-modal-tags-container">
                            {keywords.map((keyword, index) => (
                                <div key={index} className="publish-modal-tag">
                                    {keyword}
                                    <span
                                        className="publish-modal-tag-close"
                                        onClick={() => removeKeyword(keyword)}
                                    >
                                        ×
                                    </span>
                                </div>
                            ))}
                            <input
                                type="text"
                                className="publish-modal-tags-input"
                                placeholder='Digite e pressione "Enter" para adicionar novas tags.'
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={handleKeywordKeyDown}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="publish-modal-footer">
                
                {loading ? (
                    <Loader size={20}/>
                ) : (
                    <>
                        <ButtonSecondary onClick={handleClose}>
                            Cancelar
                        </ButtonSecondary>
                        <ButtonMain
                            onClick={handlePublish}
                            disabled={!deliverableName || loading}
                        >
                            Publicar no Aira Business
                        </ButtonMain>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
